import React from 'react';
import { ArrowLeft, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable } from '@/components/shared';
import { RaceContestantsViewProps } from './racecontestantspage.props';

const RaceContestantsView: React.FC<RaceContestantsViewProps> = ({
  event,
  eventRaceTypes,
  eventRegistrations,
  filteredRegistrations,
  loading,
  searchQuery, setSearchQuery,
  filterRaceType, setFilterRaceType,
  sortBy, setSortBy,
  contestantColumns,
  navigate,
}) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Skeleton className="h-10 w-24 mb-6" />
          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-1/2" />
              <Skeleton className="h-4 w-1/4 mt-2" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
              <Skeleton className="h-96 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!loading && !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Настанот не е пронајден</h2>
          <Button onClick={() => navigate('/')}>Врати се на почетна</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Назад
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">Стартна листа - {event.title}</CardTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Вкупно учесници: {filteredRegistrations.length}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Пребарувај по име или број..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={filterRaceType}
                onChange={(e) => setFilterRaceType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Сите типови на трки</option>
                {eventRaceTypes.map((rt) => (
                  <option key={rt.id} value={rt.id}>{rt.name}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="bibNumber">Сортирај по број</option>
                <option value="name">Сортирај по име</option>
              </select>
            </div>

            <DataTable
              columns={contestantColumns}
              data={filteredRegistrations}
              emptyMessage="Нема регистрирани учесници што одговараат на филтрите."
            />

            {eventRaceTypes.length > 0 && (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                {eventRaceTypes.map((rt) => {
                  const count = eventRegistrations.filter((r) => r.raceTypeId === rt.id).length;
                  return (
                    <Card key={rt.id}>
                      <CardContent className="p-4">
                        <div className="text-sm text-gray-600">{rt.name}</div>
                        <div className="text-2xl font-bold text-gray-900">{count}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {rt.distance} км • {count}/{rt.maxParticipants} учесници
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RaceContestantsView;
